import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs/Subscription";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { CustomValidators } from "app/pages/A2-utils/custom-validators";
import { IQuiz, QuizClass } from "../quiz.class";
import { QuizFormValue } from "../quiz-form-values.interface";
import { QuizService } from "../quiz.service";
import { T2LoadingService } from "app/trip2-theme/components/t2-loading/t2-loading.service";
import { SweetAlertHelper } from "app/core/helpers/sweet-alert-helper.service";
import { finalize } from "rxjs/operators";

@Component({
  selector: "app-quiz-form",
  templateUrl: "./quiz-form.component.html",
  styleUrls: ["./quiz-form.component.scss"],
})
export class QuizFormComponent implements OnInit, OnDestroy {
  quizClass: QuizClass;
  quizForm: FormGroup;
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private _serviceQuiz: QuizService,
    private sweetAlertHelper: SweetAlertHelper,
    private loading: T2LoadingService
  ) {
    this.quizForm = this.fb.group({
      quizId: [null],
      passScore: [null, [Validators.required, Validators.pattern(/^\d+(\.\d+)?$/)]],
      description: [null, [CustomValidators.required]],
      isActive: [true],
    });
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this._serviceQuiz.quizData$.subscribe({
        next: (data) => {
          this.quizClass = data;
          this.applyQuizClass();
        },
        error: (err) => {
          this.sweetAlertHelper.captureException(err);
        },
      })
    );
  }

  private applyQuizClass() {
    const val = this.quizClass;
    if (val != null && val.quizid) {
      this.quizForm.reset();
      this.quizForm.patchValue({
        quizId: val.quizid,
        description: val.description, 
        passScore: val.passscore,
        isActive: val.isactive !== false,
      });
    } else {
      this.quizForm.reset();
      this.quizForm.patchValue({ isActive: true });
    }
  }

  private mapQuizFormToApi(form: QuizFormValue): IQuiz {
    const m = new QuizClass();
    m.quizid = form.quizId;
    m.description = form.description;
    m.passscore = form.passScore;
    m.isactive = form.isActive;
    return m;
  }

  private getQuizFormValue(): QuizFormValue {
    const raw = this.quizForm.getRawValue();
    return {
      quizId: raw.quizId,
      passScore: raw.passScore,
      description: raw.description,
      isActive: raw.isActive,
    };
  }

  submitForm() {
    if (!this.quizForm.valid) {
      return;
    }
    const form = this.mapQuizFormToApi(this.getQuizFormValue());
    if (!form.quizid) {
      this.newQuiz(form);
    } else {
      this.updateQuiz(form);
    }
  }

  get quizFormId(): boolean {
    return !!(this.quizForm && this.quizForm.get("quizId") && this.quizForm.get("quizId").value);
  }

  onIsActiveChange() {
    if (!this.quizForm.get("quizId").value) {
      return;
    }
    this.updateQuiz(this.mapQuizFormToApi(this.getQuizFormValue()));
  }

  private updateQuiz(form: IQuiz) {
    if (!form.quizid) {
      return;
    }
    this.loading.show();
    this.subscriptions.push(
      this._serviceQuiz
      .putQuiz(form)
      .pipe(finalize(() => this.loading.hiden()))
      .subscribe({
        next: (res: QuizClass) => {
          if (res) {
            if (!this.quizClass) {
              this.quizClass = new QuizClass();
            }
            this.quizClass.isactive = !!res.isactive;
            this.quizForm.patchValue({ isActive: !!res.isactive });
            this._serviceQuiz.setRefreshQuizGrid(true);
            
            if (!res.isactive) {
              this._serviceQuiz.setModal(false);
            }
            
            this.sweetAlertHelper.createCustomAlert({
              type: "success",
              title: "Perfect",
              text: "Quiz updated successfully",
            });
          }
        },
        error: (err) => {
          this.sweetAlertHelper.captureException(err);
        },
      })
    );
  }

  private newQuiz(form: IQuiz) {
    this.loading.show();
    this.subscriptions.push(
      this._serviceQuiz
      .postQuiz(form)
      .pipe(finalize(() => this.loading.hiden()))
      .subscribe({
        next: (res: QuizClass) => {
          if (res) {
            this.quizForm.patchValue({ quizId: res.quizid });
            if (!this.quizClass) {
              this.quizClass = new QuizClass();
            }
            this.quizClass.quizid = res.quizid;
            this._serviceQuiz.setRefreshQuizGrid(true);
            this._serviceQuiz.setModal(false);
            this.sweetAlertHelper.createCustomAlert({
              type: "success",
              title: "Perfect",
              text: "Quiz created successfully",
            });
          }
        },
        error: (err) => {
          this.sweetAlertHelper.captureException(err);
        },
      })
    );
  }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length) {
      this.subscriptions.forEach(subscription => {
        if (subscription) {
          subscription.unsubscribe();
        }
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/quiz/quiz-form/quiz-form.component.ts